import React from 'react';
import { Link } from 'react-router-dom';

import images from 'assets/images';
import PageLayout from 'components/common/PageLayout';
import { API_ROUTE, ENTERPRISE_CONTACT_LINK, SECURITY_ROUTE } from 'components/constants';

import styles from './Hosting.module.scss';

const hostingPillars = [
  {
    number: '01',
    title: 'Managed publishing',
    text: 'Publish from Storycards without provisioning and maintaining a separate hosting environment for each interactive product.',
  },
  {
    number: '02',
    title: 'Global delivery',
    text: 'Serve products through Cloudflare’s global content delivery network, backed by Amazon Web Services infrastructure.',
  },
  {
    number: '03',
    title: 'Traffic surge readiness',
    text: 'Infrastructure scales when a campaign, live event or news moment brings a sudden increase in audience traffic.',
  },
  {
    number: '04',
    title: 'Hosting-layer protection',
    text: 'Built-in SSL/TLS certificates, DDoS protection and bot filtering help protect every published product and its audience.',
  },
];

const mediaCapabilities = [
  {
    title: 'Automatic image optimization',
    text: 'Optimize images for delivery without changing the source file your team manages in Studio.',
  },
  {
    title: 'Browser-aware image formats',
    text: 'Serve the image format supported by the visitor’s browser and device, including WebP, PNG and JPEG.',
  },
  {
    title: 'Responsive image sizing',
    text: 'Deliver an appropriately scaled asset for desktop, tablet or mobile instead of sending the same image to every screen.',
  },
  {
    title: 'Adaptive video streaming',
    text: 'Deliver video with adaptive bitrate streaming, low-latency delivery and HLS support.',
  },
];

const hostingDetails = [
  { label: 'Published-product uptime', value: '99%' },
  { label: 'Cloud infrastructure', value: 'Amazon Web Services' },
  { label: 'Content delivery', value: 'Cloudflare global CDN' },
  { label: 'Delivery protocols', value: 'HTTP/2 and HTTP/3' },
  { label: 'Transport security', value: 'Built-in SSL/TLS certificates' },
  { label: 'Traffic protection', value: 'DDoS protection and bot filtering' },
];

const publishingSteps = [
  {
    title: 'Approve the product in Studio',
    text: 'Complete the responsive layouts, content, logic and publishing review inside the same governed workspace.',
  },
  {
    title: 'Publish to managed infrastructure',
    text: 'Move the approved version live without setting up a separate deployment and hosting stack for that product.',
  },
  {
    title: 'Deliver and connect',
    text: 'Serve the experience through the global CDN, then transfer selected interaction data to the systems your team controls.',
  },
];

const Hosting: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Managed hosting for interactive products</p>
            <h1>Publish once. Deliver to every audience.</h1>
            <p className={styles.heroText}>
              Move from an approved product in Studio to a live experience on managed infrastructure. Storycards
              handles global delivery, traffic surges, certificates, hosting-layer protection and media optimization,
              so your team can focus on the product instead of a separate hosting stack.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <aside className={styles.heroPromise} aria-label="Storycards hosting overview">
            <span>Built into the publishing flow</span>
            <div>
              <strong>One path from Studio to live</strong>
              <p>Publish an approved product without creating a separate infrastructure project.</p>
            </div>
            <div>
              <strong>Delivery designed for changing demand</strong>
              <p>Use the same managed hosting for an always-on journey, a campaign or a high-traffic live moment.</p>
            </div>
          </aside>
        </div>

        <figure className={styles.heroVisual}>
          <img
            src={images.hostingHero}
            alt="Managed cloud infrastructure for globally delivered Storycards products"
          />
          <figcaption className={styles.networkStatus}>
            <strong>Ready when your audience arrives.</strong>
            <div>
              <em>Cloudflare CDN</em>
              <em>HTTP/3</em>
              <em>SSL/TLS</em>
            </div>
          </figcaption>
        </figure>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>No separate infrastructure project before launch.</h2>
          <div>
            <p>
              Interactive products have the same operational requirements as the rest of your digital estate. They
              need predictable publishing, fast asset delivery, secure transport and a clear path for collected data.
            </p>
            <p>
              Storycards keeps those requirements inside the platform. Creative and business teams can publish from
              Studio, while technical teams retain a concrete view of the infrastructure, protocols and data path.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.pillarsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.pillarsHeader}>
            <p className={styles.eyebrow}>Hosting capabilities</p>
            <h2>Ready for the everyday journey and the high-traffic moment.</h2>
          </header>
          <div className={styles.pillarsGrid}>
            {hostingPillars.map((pillar) => (
              <article key={pillar.title}>
                <span>{pillar.number}</span>
                <h3>{pillar.title}</h3>
                <p>{pillar.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.mediaSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>Media delivery</p>
              <h2>Send the right asset for the screen and connection.</h2>
            </div>
            <p>
              Storycards prepares images and video for delivery as part of the hosting flow. Teams keep working with
              the source assets in Studio while published products serve more appropriate output to the audience.
            </p>
          </header>
          <div className={styles.mediaGrid}>
            {mediaCapabilities.map((capability, index) => (
              <article key={capability.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{capability.title}</h3>
                <p>{capability.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.detailsSection}>
        <div className={styles.sectionInner}>
          <header className={styles.detailsHeader}>
            <div>
              <p className={styles.eyebrow}>Infrastructure details</p>
              <h2>The hosting layer, stated clearly.</h2>
            </div>
            <p>
              Storycards securely processes and manages billions of page views each month. The published-product
              hosting service provides the following delivery and protection capabilities.
            </p>
          </header>
          <dl className={styles.detailsList}>
            {hostingDetails.map((detail) => (
              <div key={detail.label}>
                <dt>{detail.label}</dt>
                <dd>{detail.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className={styles.dataSection}>
        <div className={styles.sectionInner}>
          <header className={styles.dataHeader}>
            <p className={styles.eyebrow}>Data path and security</p>
            <h2>Hosting the experience does not dictate where its data must live.</h2>
            <p>
              Configure hosting and data handling to support your organization’s security and GDPR requirements, then
              choose how selected interaction data reaches the systems your teams operate.
            </p>
          </header>
          <div className={styles.dataGrid}>
            <article>
              <span>Data Bridge</span>
              <h3>Transfer collected data directly to your environment.</h3>
              <p>
                Send data to your databases or servers without storing it in the Storycards cloud. Your team defines
                the destination and the logic applied after delivery.
              </p>
              <Link to={API_ROUTE}>Explore the API and data connections <span aria-hidden="true">→</span></Link>
            </article>
            <article>
              <span>Security controls</span>
              <h3>Connect hosting decisions to your wider security model.</h3>
              <p>
                Review access policies, encryption, authentication and data controls alongside the protections built
                into the hosting layer.
              </p>
              <Link to={SECURITY_ROUTE}>Explore Storycards security <span aria-hidden="true">→</span></Link>
            </article>
          </div>
        </div>
      </section>

      <section className={styles.workflowSection}>
        <div className={styles.sectionInner}>
          <header className={styles.workflowHeader}>
            <p className={styles.eyebrow}>From approved to available</p>
            <h2>A shorter operating path from design to audience.</h2>
          </header>
          <div className={styles.workflowGrid}>
            {publishingSteps.map((step, index) => (
              <article key={step.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Plan the hosting model behind your next Storycards launch.</h2>
          <div>
            <p>
              Talk with our enterprise team about expected traffic, media delivery, security requirements and the data
              path behind your interactive products.
            </p>
            <div className={styles.ctaActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default Hosting;
